import cn from "classnames";
import React, { type MutableRefObject, type ReactNode, useMemo, useRef } from "react";
import { type IObservable } from "../../../../../interfaces";
import { type IFlowInfo } from "../../Info/types";

import { ReactFlowProvider } from "reactflow";
import { type FlowValidator } from "../validator/FlowValidator";
import { ReactFlowCanvasProvider } from "../ReactFlowCanvas/Providers/ReactFlowCanvasProvider/ReactFlowCanvasProvider";
import { Loader } from "semantic-ui-react";
import { ErrorNavigationProvider } from "../FlowComponentsPanel/Providers/ErrorNavigationProvider";
import { type EntityTriggerTypes, type SavedFlowData, type TriggerTimeUnit } from "../types";
import { type IFlowEdge, type IFlowNode } from "../ReactFlowCanvas/nodes/types";

import ControlPanel from "../ControlPanel/ControlPanel";
import ReactFlowCanvas from "../ReactFlowCanvas/ReactFlowCanvas";
import AssetDragLayer from "../AssetDragLayer/AssetDragLayer";
import AssetInspector from "../AssetInspector/AssetInspector";

import "./DropAreaReactFlow.scss";
import { ReactFlowStateProvider } from "../ReactFlowCanvas/Providers/ReactFlowStateProvider/ReactFlowStateProvider";
import { ViewContentLinkButton } from "components";
import { getContentLinkToEupFromPreview } from "utils/eupUtils";

export interface IDropAreaReactFlowOwnProps {
  flowInfo: IFlowInfo;
  flowDefinition: SavedFlowData;
  flowDefinitionLoading: boolean;
  triggerTypes: EntityTriggerTypes[];
  triggerTimeUnits: TriggerTimeUnit[];
  isReadOnly: boolean;
  onSavingProcessBegin: () => void;
  onSavingProcessSuccess: (id?: string) => void;
  flowValidator: FlowValidator;
  onResetSelectedElementsObserver?: IObservable<() => void>;
  flowDataRef: MutableRefObject<{ nodes: IFlowNode[]; edges: IFlowEdge[]; headId?: string } | undefined>;
  externalTriggerGroupsLength: number;
  canView?: boolean;
}

const DropAreaReactFlow: React.FC<IDropAreaReactFlowOwnProps> = ({
  flowInfo,
  flowDefinition,
  flowDefinitionLoading,
  triggerTypes,
  triggerTimeUnits,
  onSavingProcessBegin,
  onSavingProcessSuccess,
  isReadOnly,
  flowValidator,
  onResetSelectedElementsObserver,
  flowDataRef,
  externalTriggerGroupsLength,
  canView,
}): React.ReactElement => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  const viewButton = useMemo<ReactNode>(() => {
    if (canView && flowInfo?.hasConsumableItems) {
      return (
        <div className="view-content-container">
          <ViewContentLinkButton
            href={getContentLinkToEupFromPreview["Flows"](flowInfo.id)}
            additionalRootClassname="view-content-link-button"
            fitted
          />
        </div>
      );
    }
    return null;
  }, [canView, flowInfo?.id, flowInfo?.hasConsumableItems]);

  return (
    <div className="drop-area-react-flow">
      {flowDefinitionLoading && <Loader active />}
      {flowDefinition && (
        <ReactFlowProvider>
          <ReactFlowStateProvider flowData={flowDefinition}>
            <ReactFlowCanvasProvider
              flowId={flowInfo?.id}
              flowDataHeadId={flowDefinition.headId}
              isReadOnly={isReadOnly}
              onSavingProcessBegin={onSavingProcessBegin}
              onSavingProcessSuccess={onSavingProcessSuccess}
              flowValidator={flowValidator}
              flowDataRef={flowDataRef}
              reactFlowWrapper={reactFlowWrapper}
            >
              <AssetDragLayer />
              <ControlPanel
                flowData={flowDefinition}
                externalTriggerGroupsLength={externalTriggerGroupsLength}
                viewButton={viewButton}
              />
              <div
                id="infinite-canvas"
                className={cn("drop-area-react-flow__infinite-canvas", {
                  readonly: isReadOnly,
                })}
              >
                <ErrorNavigationProvider>
                  <ReactFlowCanvas
                    triggerTypes={triggerTypes}
                    triggerTimeUnits={triggerTimeUnits}
                    isReadOnly={isReadOnly}
                    reactFlowWrapper={reactFlowWrapper}
                  />
                  <AssetInspector
                    triggerTypes={triggerTypes}
                    triggerTimeUnits={triggerTimeUnits}
                    isReadOnly={isReadOnly}
                    onResetSelectedElementsObserver={onResetSelectedElementsObserver}
                  />
                </ErrorNavigationProvider>
              </div>
            </ReactFlowCanvasProvider>
          </ReactFlowStateProvider>
        </ReactFlowProvider>
      )}
    </div>
  );
};

export default DropAreaReactFlow;
